import { api, get } from "./client.js";

/**
 * Public API client — every read endpoint the frontend calls is enumerated
 * here so refactors stay easy and the surface is auditable.
 */

/* ---- Settings (page-visibility only — brand info is hard-coded) ---- */
export const fetchSiteSettings = async () => (await get("/public/settings")).data;

/* ---- Designs ---- */
export const fetchDesigns = async (params) => {
  const r = await get("/public/designs", params);
  return { items: r.data ?? [], meta: r.meta };
};
export const fetchDesignBySlug = async (slug) => (await get(`/public/designs/${slug}`)).data;
export const fetchFeaturedDesigns = async (limit = 6) =>
  (await get("/public/designs/featured", { limit })).data;

/* ---- Facades ---- */
export const fetchFacades = async () => (await get("/public/facades")).data;
export const fetchFeaturedFacades = async (limit = 6) =>
  (await get("/public/facades/featured", { limit })).data;

/* ---- Floor Plans ---- */
export const fetchFloorPlans = async () => (await get("/public/floor-plans")).data;
export const fetchFeaturedFloorPlans = async (limit = 6) =>
  (await get("/public/floor-plans/featured", { limit })).data;

/* ---- Gallery (Interior / Exterior photos managed by admin) ---- */
export const fetchGallery = async (params) =>
  (await get("/public/gallery", params)).data;
/**
 * Featured gallery photos for the homepage strips. Pass `section` to
 * limit the result to "interior" or "exterior".
 */
export const fetchFeaturedGallery = async ({ limit = 6, section } = {}) =>
  (await get("/public/gallery/featured", { limit, ...(section ? { section } : {}) })).data;

/* ---- Display Homes ---- */
export const fetchDisplayHomes = async (params) => {
  const r = await get("/public/display-homes", params);
  return { items: r.data ?? [], meta: r.meta };
};
export const fetchDisplayHome = async (slug) => (await get(`/public/display-homes/${slug}`)).data;

/* ---- House & Land ---- */
export const fetchHouseLand = async (params) => {
  const r = await get("/public/house-land", params);
  return { items: r.data ?? [], meta: r.meta };
};
export const fetchHouseLandPackage = async (slug) => (await get(`/public/house-land/${slug}`)).data;
export const fetchSuburbs = async () => (await get("/public/house-land/suburbs")).data;

/* ---- Services ---- */
export const fetchServices = async () => (await get("/public/services")).data;
export const fetchService = async (slug) => (await get(`/public/services/${slug}`)).data;
export const fetchFeaturedServices = async (limit = 6) =>
  (await get("/public/services/featured", { limit })).data;

/* ---- Blogs ---- */
export const fetchBlogCategories = async () => (await get("/public/blog-categories")).data;
export const fetchBlogs = async (params) => {
  const r = await get("/public/blogs", params);
  return { items: r.data ?? [], meta: r.meta };
};
export const fetchBlog = async (slug) => (await get(`/public/blogs/${slug}`)).data;
export const fetchLatestBlogs = async (limit = 3) =>
  (await get("/public/blogs/latest", { limit })).data;
export const fetchFeaturedBlogs = async (limit = 3) =>
  (await get("/public/blogs/featured", { limit })).data;

/* ---- Matterport ---- */
export const fetchMatterport = async (params) => {
  const r = await get("/public/matterport", params);
  return { items: r.data ?? [], meta: r.meta };
};
export const fetchMatterportListing = async (slug) =>
  (await get(`/public/matterport/${slug}`)).data;
export const fetchFeaturedMatterport = async (limit = 6) =>
  (await get("/public/matterport/featured", { limit })).data;

/* ---- About ---- */
export const fetchAwards = async () => (await get("/public/awards")).data;
export const fetchTeam = async () => (await get("/public/team")).data;
export const fetchTestimonials = async (params) => (await get("/public/testimonials", params)).data;

/* ---- Forms ---- */
// Static import (was dynamic) — the await import("./client.js") version had
// intermittent timing issues that surfaced as "field required" errors when
// the request fired before the module was ready.
export const submitForm = async (payload) => {
  const response = await api.post("/public/forms/submit", payload);
  return response.data?.data;
};
